import { motion } from "framer-motion";
// import { ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import {
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
} from "lucide-react";
import InfoIcon from "../assets/images/info.png";
import { useEffect, useRef, useState } from "react";
import DateRangeFilter from "./ui/datepicker";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Spin } from "antd";

const Meters = ({
  currentData,
  totalItems,
  currentPage,
  totalPages,
  // pageLimit,
  // setPageLimit,
  handlePageChange,
  handleSort,
  sortColumn,
  sortDirection,

  searchTerm,
  setSearchTerm,
  filterOpen,
  setFilterOpen,
  activeFilter,
  setActiveFilter,
  operationalFilter,
  setOperationalFilter,
  month,
  setMonth,
  year,
  setYear,
  isSearchMode,
  statusToggle, // Receive from Parent
  setStatusToggle, // Receive from Parent
  loading,
}) => {
  const filterRef = useRef(null);
  const [openTooltip, setOpenTooltip] = useState(null);
  const [filteredData, setFilteredData] = useState(currentData);
  // const [statusToggle, setStatusToggle] = useState(false); // false = All, true = Active
  const { theme, colors } = useTheme();
  const navigate = useNavigate();
  const tooltipRef = useRef(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const handleToggle = () => {
    const newValue = !statusToggle;
    setStatusToggle(newValue);
    handlePageChange(1); // Reset to first page on data change
  };
  const goToMeterDetails = (meterId) => {
    const thirtyDaysBack = dayjs().subtract(30, "day").format("YYYY-MM-DD");

    navigate(
      `/meter-leak-details?category=ALL&meter_id=${meterId}&time_range=Current&selected_time=${thirtyDaysBack}`,
      {
        state: {
          water_loss_gallons: null,
          price: null,
          leak_rate: null,
        },
      },
    );
  };
  const displayCount =
    searchTerm || activeFilter.length > 0 ? filteredData.length : totalItems;
  const [pageLimit, setPageLimit] = useState(
    window.innerWidth >= 1920 ? 20 : 9,
  );
  const dynamicTotalPages = Math.ceil(displayCount / pageLimit);
  // Filter the data based on the month and year
  useEffect(() => {
    let filtered = [...currentData];

    // Apply active status filter (green represents active, red represents inactive)
    if (activeFilter.length) {
      filtered = filtered.filter((item) => {
        if (activeFilter.includes("active") && item.active === true) {
          return true; // Keep this item if it's active (true)
        }
        if (activeFilter.includes("inactive") && item.active === false) {
          return true; // Keep this item if it's inactive (false)
        }
        return false; // Exclude item if it doesn't match the active/inactive filter
      });
    }

    // Apply operational status filter (green, orange, red)
    if (operationalFilter.length) {
      filtered = filtered.filter((item) =>
        item.operational
          ? operationalFilter.includes(item.operational.toLowerCase())
          : false,
      );
    }

    // Apply month and year filter
    // Apply month filter using API date_range
    if (month && year) {
      filtered = filtered.filter((item) => {
        if (!item.dateRange) return false;

        const parts = item.dateRange.split(" - ");
        if (parts.length !== 2) return false;

        const [startStr, endStr] = parts;

        const parseMonthYear = (str) => {
          const [monthName, yearValue] = str.split(" ");
          const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
          return new Date(yearValue, monthIndex);
        };

        const startDate = parseMonthYear(startStr);
        const endDate = parseMonthYear(endStr);

        const selectedDate = new Date(year, month - 1);

        return selectedDate >= startDate && selectedDate <= endDate;
      });
    }

    // Apply date range filter
    // if (dateRange.startDate && dateRange.endDate) {
    //   filtered = filtered.filter((item) => {
    //     const itemDate = new Date(item.dateRange);
    //     return itemDate >= dateRange.startDate && itemDate <= dateRange.endDate;
    //   });
    // }

    setFilteredData(filtered); // Set filtered data to the state
  }, [currentData, activeFilter, operationalFilter, month, year, dateRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks inside filter OR tooltip
      if (
        filterRef.current?.contains(event.target) ||
        tooltipRef.current?.contains(event.target)
      )
        return;

      // Ignore AntD picker
      if (event.target.closest(".ant-picker-dropdown")) return;

      // Close both
      setFilterOpen(false);
      setOpenTooltip(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getPageNumbers = (currentPage, totalPages) => {
    const pages = new Set();

    // Always show first 2 pages
    pages.add(1);
    pages.add(2);

    // Always show last 2 pages
    pages.add(totalPages);
    pages.add(totalPages - 1);

    // Show current page & neighbors
    pages.add(currentPage);
    pages.add(currentPage - 1);
    pages.add(currentPage + 1);

    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1920) {
        setPageLimit(20);
      } else {
        setPageLimit(9);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    handlePageChange(1);
  }, [pageLimit]);

  return (
    <motion.div
      className="backdrop-blur-sm rounded-xl border overflow-hidden min-h-140"
      style={{
        background: colors.cardBg,
        borderColor: colors.border,
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Top Bar */}
      <div
        className="flex items-center justify-between p-4 border-b flex-wrap gap-3"
        style={{ borderColor: colors.border }}
      >
        <div className="flex items-center gap-6">
          <div className="font-semibold text-lg" style={{ color: colors.text }}>
            Meters
          </div>

          {/* Toggle Switch Container */}
          <div className="flex items-center gap-3">
            {/* Label for the 'Start' position (statusToggle = true) */}
            <span
              className="text-xs sm:text-sm font-medium"
              style={{
                color: statusToggle ? colors.text : colors.textSecondary,
              }}
            >
              Active
            </span>

            <button
              onClick={handleToggle}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200"
              style={{
                // We use colors.primary when pointing to "All Meters" (statusToggle = false)
                backgroundColor: !statusToggle
                  ? colors.primary
                  : `${colors.primary}20`,
              }}
            >
              <span
                className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                style={{
                  // Circle moves right (1.5rem) when statusToggle is false (pointing to All Meters)
                  transform: !statusToggle
                    ? "translateX(1.5rem)"
                    : "translateX(0.25rem)",
                }}
              />
            </button>

            {/* Label for the 'End' position (statusToggle = false) */}
            <span
              className="text-xs sm:text-sm font-medium"
              style={{
                color: !statusToggle ? colors.text : colors.textSecondary,
              }}
            >
              All Meters
            </span>
          </div>
        </div>
        {/* Right Side Search + Filter */}
        <div className="flex items-center gap-3">
          {/* 🔍 Search */}
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded px-3 py-2 text-sm outline-none w-56 border"
            style={{
              background: colors.background,
              borderColor: colors.border,
              color: colors.text,
            }}
          />

          {/* 🎛 Filter Button */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="rounded px-3 py-2 text-sm border"
              style={{
                background: colors.background,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              Filters
            </button>

            {filterOpen && (
              <div
                className="absolute right-0 mt-2 w-72  border rounded-lg shadow-lg p-4 z-50"
                style={{
                  background: colors.background,
                  borderColor: colors.border,
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className=" font-semibold text-lg"
                    style={{ color: colors.text }}
                  >
                    Filters
                  </div>

                  <div
                    className="w-5 h-5  cursor-pointer hover:text-white transition"
                    style={{ color: colors.textSecondary }}
                    onClick={() => setFilterOpen(false)}
                  >
                    X
                  </div>
                </div>
                {/* Active Status */}
                <div className="mb-4">
                  <div
                    className="text-sm font-semibold mb-2"
                    style={{ color: colors.text }}
                  >
                    Active Status
                  </div>

                  {["active", "inactive"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 text-[16px] mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      <input
                        type="checkbox"
                        className="w-5 h-5 accent-[#00b8f1] cursor-pointer"
                        style={{ borderColor: colors.border }}
                        checked={activeFilter.includes(status)}
                        onChange={() => {
                          setActiveFilter((prev) =>
                            prev.includes(status)
                              ? prev.filter((s) => s !== status)
                              : [...prev, status],
                          );
                        }}
                      />
                      {status}
                    </label>
                  ))}
                </div>

                {/* Operational */}
                <div className="mb-4">
                  <div
                    className="text-sm font-semibold mb-2"
                    style={{ color: colors.text }}
                  >
                    Operational Status
                  </div>

                  {["green", "orange", "red"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 text-[16px] mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      <input
                        type="checkbox"
                        className="w-5 h-5 accent-[#00b8f1] cursor-pointer"
                        style={{ borderColor: colors.border }}
                        checked={operationalFilter.includes(status)}
                        onChange={() => {
                          setOperationalFilter((prev) =>
                            prev.includes(status)
                              ? prev.filter((s) => s !== status)
                              : [...prev, status],
                          );
                        }}
                      />
                      {status}
                    </label>
                  ))}
                </div>

                {/* Month & Year */}
                <div className="mb-4">
                  {/* <DateRangeFilter
                    month={month}
                    setMonth={setMonth}
                    year={year}
                    setYear={setYear}
                    setDateRange={setDateRange} 
                  /> */}
                </div>

                {/* Reset */}
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setActiveFilter([]);
                    setOperationalFilter([]);
                    setMonth("");
                    setYear("");
                    setDateRange({ startDate: null, endDate: null }); // Reset date range
                    handlePageChange(1);
                    setFilterOpen(false);
                  }}
                  className="w-25 py-2 rounded text-sm m-auto block"
                  style={{ color: colors.text, background: colors.primary }}
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading && (
          <div
            className="absolute inset-0  backdrop-blur-sm flex items-start justify-center z-40 pt-[25px]"
            style={{
              background: `${colors.background}cc`,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <table className="w-full">
          <thead
            style={{
              backgroundColor:
                theme === "white" ? "#f8f9fa" : `${colors.primary}08`,
              borderTop: `1px solid ${colors.border}`,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <tr className="border-b" style={{ borderColor: colors.border }}>
              {[
                "active",
                "operational",
                "account",
                "meterId",
                "register",
                "meter_sn",
                "dateRange",
                "meterType",
                "model",
                "size",
                "dials",
              ].map((col) => (
                <th
                  key={col}
                  className="py-4 px-4  font-semibold text-sm text-left relative group"
                  style={{ color: colors.textSecondary }}
                >
                  {/* <button
                    onClick={() => handleSort(col)}
                    className="flex items-center gap-1 uppercase tracking-wider text-[12px]"
                  > */}
                  <button
                    onClick={() => handleSort(col)}
                    className="flex items-center gap-2 select-none uppercase tracking-wider text-[12px]"
                  >
                    {/* {col.replace(/([A-Z])/g, " $1")} */}
                    {col === "meter_sn"
                      ? "METER SN"
                      : col.replace(/([A-Z])/g, " $1")}
                    {/* {sortColumn === col &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))} */}
                    {sortColumn === col ? (
                      sortDirection === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}

                    {/* Info icon only for Active column */}
                    {col === "active" && (
                      <span
                        className="ml-1 text-[#00b8f1] text-xs cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltip(openTooltip === col ? null : col);
                        }}
                      >
                        {/* <img src={InfoIcon} alt="Info" className="w-4 h-4" /> */}
                        <button
                          className="flex items-center justify-center w-5 h-5 rounded-full transition-all duration-200 hover:scale-110"
                          style={{
                            backgroundColor: "rgba(0, 184, 241, 0.082)",
                            color: "rgb(0, 184, 241)",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3 h-3"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                          </svg>
                        </button>
                      </span>
                    )}
                    {col === "operational" && (
                      <span
                        className="ml-1 text-[#00b8f1] text-xs cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltip(openTooltip === col ? null : col);
                        }}
                      >
                        {/* <img src={InfoIcon} alt="Info" className="w-4 h-4" /> */}
                        <button
                          className="flex items-center justify-center w-5 h-5 rounded-full transition-all duration-200 hover:scale-110"
                          style={{
                            backgroundColor: "rgba(0, 184, 241, 0.082)",
                            color: "rgb(0, 184, 241)",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3 h-3"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                          </svg>
                        </button>
                      </span>
                    )}
                  </button>

                  {/* Tooltip only for Active */}
                  {col === "active" && openTooltip === "active" && (
                    <div
                      ref={tooltipRef}
                      className="absolute left-0 top-full mt-2 w-56 border rounded-lg shadow-lg text-[13px] p-3 transition-opacity z-50"
                      style={{
                        color: colors.text,
                        background: colors.background,
                        borderColor: colors.border,
                      }}
                    >
                      <div className="font-semibold text-[15px] mb-3">
                        Depends on the Endpoint Status
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full bg-green-500" />
                        <span className="text-[15px]">Active</span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full bg-red-500" />
                        <span className="text-[15px]">Inactive</span>
                      </div>
                    </div>
                  )}
                  {col === "operational" && openTooltip === "operational" && (
                    <div
                      className="absolute left-0 top-full mt-2 w-56 border rounded-lg shadow-lg text-[13px] p-3 transition-opacity z-50"
                      style={{
                        color: colors.text,
                        background: colors.background,
                        borderColor: colors.border,
                      }}
                    >
                      <div className="font-semibold text-[15px] mb-3">
                        Operational Status
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full bg-green-500" />
                        <span className="text-[15px]">Healthy</span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full bg-red-500" />
                        <span className="text-[15px]">Offline</span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full bg-orange-500" />
                        <span className="text-[15px]">Delayed</span>
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.slice(0, pageLimit).map((meter, idx) => (
              // <tr
              //   key={idx}
              //   className="border-b transition-colors"
              //   onMouseEnter={(e) =>
              //     (e.currentTarget.style.background = `${colors.primary}10`)
              //   }
              //   onMouseLeave={(e) =>
              //     (e.currentTarget.style.background = "transparent")
              //   }
              //   style={{ borderColor: colors.border }}
              // >
              <tr
                key={idx}
                className="border-b transition-colors cursor-pointer"
                onClick={() => goToMeterDetails(meter.meterId)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = `${colors.primary}10`)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
                style={{ borderColor: colors.border }}
              >
                <td className="py-4 px-4">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      meter.active ? "bg-green-500" : "bg-red-500"
                    }`}
                  />
                </td>
                <td className="py-4 px-4">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      meter.operational === "orange"
                        ? "bg-orange-500"
                        : meter.operational === "red"
                          ? "bg-red-500"
                          : "bg-green-500"
                    }`}
                  />
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.account}
                </td>
                <td className="py-4 px-4 text-sm">
                  <span
                    className="font-bold underline decoration-1 underline-offset-4"
                    style={{ color: colors.primary }}
                  >
                    {meter.meterId}
                  </span>
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.register}
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.meter_sn ?? meter.metersn}
                </td>

                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.dateRange}
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.meterType}
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.model}
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.size}
                </td>
                <td
                  className="py-4 px-4 text-sm"
                  style={{ color: colors.text }}
                >
                  {meter.dials}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          className="flex items-center justify-between p-4 border-t"
          style={{
            borderColor: colors.border,
            backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
          }}
        >
          {/* Entries Info */}
          <div className="text-xs" style={{ color: colors.textSecondary }}>
            Showing {Math.min((currentPage - 1) * pageLimit + 1, displayCount)}{" "}
            to {Math.min(currentPage * pageLimit, displayCount)} of{" "}
            {displayCount} records
          </div>

          <div className="flex items-center gap-6">
            {/* Page Navigation Controls */}
            {dynamicTotalPages > 0 && (
              <div className="flex items-center gap-2">
                {/* Prev Button */}
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  className="p-1.5 rounded-md border transition-all"
                  style={{
                    borderColor:
                      currentPage === 1 ? colors.border : colors.primary,
                    color:
                      currentPage === 1 ? colors.textSecondary : colors.primary,
                    opacity: currentPage === 1 ? 0.3 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Page Selector Dropdown */}
                <div className="flex items-center gap-2 px-2">
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Page
                  </span>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="text-sm rounded-md px-2 py-1 outline-none cursor-pointer"
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      borderColor: colors.border,
                      borderWidth: "1px",
                      color: colors.text,
                    }}
                  >
                    {Array.from(
                      { length: dynamicTotalPages },
                      (_, i) => i + 1,
                    ).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    of {dynamicTotalPages}
                  </span>
                </div>

                {/* Next Button */}
                <button
                  disabled={currentPage === dynamicTotalPages}
                  onClick={() =>
                    handlePageChange(
                      Math.min(currentPage + 1, dynamicTotalPages),
                    )
                  }
                  className="p-1.5 rounded-md border transition-all"
                  style={{
                    borderColor:
                      currentPage === dynamicTotalPages
                        ? colors.border
                        : colors.primary,
                    color:
                      currentPage === dynamicTotalPages
                        ? colors.textSecondary
                        : colors.primary,
                    opacity: currentPage === dynamicTotalPages ? 0.3 : 1,
                    cursor:
                      currentPage === dynamicTotalPages
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Rows Per Page Selector */}
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: colors.textSecondary }}
            >
              <span>Rows:</span>
              <select
                value={pageLimit}
                onChange={(e) => {
                  setPageLimit(Number(e.target.value));
                  handlePageChange(1); // Reset to page 1
                }}
                className="rounded px-2 py-1 text-sm border outline-none cursor-pointer"
                style={{
                  background: theme === "white" ? "#ffffff" : colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                <option value={9}>9</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Meters;
